<template>
  <div v-if="load === 'success'" class="calc">
    <the-header />
    <div class="calc__container">
      <gl-video-embed
        :url="`https://youtu.be/${videos[$i18n.locale] || videos.en}`"
        class="calc__video"
      />
      <calc-profit class="calc__profit" />
      <calc-filter class="calc__filter" />
      <calc-socials :profile="profile" class="calc__socials" />
    </div>
    <footer-menu />
  </div>
  <div v-else-if="load === 'error'" style="padding: 32px;">
    <gl-message variant="error" :message="$t('Unexpected Error!')" />
  </div>
  <gl-loader v-else is-centered />
</template>

<script>
import GlVideoEmbed from "@gl/GlVideoEmbed/index";
import GlLoader from "@gl/GlLoader";
import GlMessage from "@gl/GlMessage";

import CalcFilter from "@/components/Calc/CalcFilter";
import CalcProfit from "@/components/Calc/CalcProfit";
import CalcSocials from "@/components/Calc/CalcSocials";
import { getFactor, getPackages, getProfile, getRate } from "@/plugins/axios";
import { usedPacks } from "@/config";
import { packageCodeToName } from "@/tools";
import FooterMenu from "@/components/Menu/FooterMenu/index";
import TheHeader from "@/components/Menu/TheHeader/index";
import BigNumber from "bignumber.js";

export default {
  name: "Calc",
  components: {
    TheHeader,
    FooterMenu,
    CalcSocials,
    CalcFilter,
    CalcProfit,
    GlVideoEmbed,
    GlLoader,
    GlMessage,
  },
  data() {
    return {
      load: "loading",
      profile: {},
      videos: {
        en: "PBEqe52Twro",
        de: "i0dut4uMU4g",
        hi: "K2N50sM9elo",
        ru: "gTDKXjnAQ88",
        es: "7RwV_Y-slvQ",
      },
    };
  },
  async created() {
    const load = await Promise.all([
      this.loadPackages(),
      this.loadRate(),
      this.loadProfile(),
      this.loadFactor(),
    ]);
    if (!load[1]) {
      await this.$router.push({ name: "home" });
    } else if (load[0]) {
      this.load = "success";
    } else {
      this.load = "error";
    }
  },
  methods: {
    async loadPackages() {
      const { data } = await getPackages();
      if (data.state === "success") {
        const packageAll = [];
        usedPacks.forEach((packCode) => {
          const pack = data.data.packs.find((pack) => pack.code === packCode);
          if (pack) {
            packageAll.push({
              license: new BigNumber(pack.currencyAmount).div(1.1).toNumber(),
              price: pack.currencyAmount,
              availableMaxLoad: pack.maxLoad,
              licenseName:
                packageCodeToName(pack.code).toUpperCase() +
                ": " +
                pack.currencyAmount +
                " EUR",
              licenseNameShort: pack.currencyAmount + " EUR",
            });
          }
        });
        this.$store.commit("setPackageAll", packageAll);
        return true;
      }
      return false;
    },
    async loadRate() {
      const { data } = await getRate({
        exchange: "COINSBIT",
        rateType: "GENERIC",
        from: "ULTIMA",
        to: "EUR",
      });
      if (data.state === "success") {
        this.$store.commit(
          "setRate",
          Number.parseFloat(data.data.rate.toFixed(2))
        );
        return true;
      }
      return false;
    },
    async loadProfile() {
      const inviteCode = this.$route.params.invite;
      const { data } = await getProfile({ inviteCode });
      if (data.state === "success") {
        this.profile = data.data;
        return true;
      }
      return false;
    },
    async loadFactor() {
      const { data } = await getFactor({ coinName: "ULTIMA" });
      if (data.state === "success") {
        this.$store.commit(
          "setFreezeMonthAll",
          data.data.map((i, index) => ({
            ...i,
            code: index + 1,
            index: i.factor,
          }))
        );
        return true;
      }
      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
.calc {
  width: 100%;

  &::before {
    background: url("~img/bg-x-header.svg") center top no-repeat,
      linear-gradient(135deg, #0d5bdb 0%, #0a439f 100%);
    background-size: 466px 390px, cover;
    content: "";
    height: 390px;
    position: absolute;
    width: 100%;

    @media (min-width: $breakpoint-md) {
      background-size: 532px 532px, cover;
      background-position: top right;
      height: 532px;
    }
  }

  &__container {
    display: grid;
    gap: 16px;
    padding: 16px 16px 96px;
    position: relative;
    width: 100%;

    @media (min-width: $breakpoint-md) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-template-rows: auto 1fr;
      margin: 0 auto;
      max-width: 704px;
      padding: 16px 0 96px;
    }

    @media (min-width: $breakpoint-xl) {
      grid-template-columns: 540px 1fr;
      gap: 16px 32px;
      max-width: 968px;
    }
  }

  &__filter {
    @media (min-width: $breakpoint-md) {
      grid-row: 1/3;
    }
  }

  &__socials {
    grid-column: 1/-1;
    margin-top: 16px;
  }

  &__profit {
    @media (min-width: $breakpoint-md) {
      align-self: flex-start;
    }
  }
}
</style>
